/* eslint-disable no-nested-ternary */
import React from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AccordionDetails, AccordionSummary } from "@mui/material";
import { CgProfile } from "react-icons/cg";
import { CARGO_CODES } from "constants/permissions";
import { MdFace6 } from "react-icons/md";
import { FaRegStar, FaCalendarAlt, FaRegCalendarCheck } from "react-icons/fa";

import {
  IoIdCardOutline,
  IoVolumeHighOutline,
  IoPeople,
  IoStorefrontOutline,
  IoLogoWhatsapp,
} from "react-icons/io5";
import { RiHotelLine } from "react-icons/ri";
import { ModalNovaSugestao } from "screens/revenda/components/SugestaoModal/sugestaoModal.component";
import { BiCommentAdd } from "react-icons/bi";
import { FaPeopleGroup } from "react-icons/fa6";
import { LuUserPlus } from "react-icons/lu";
import * as IconsSVG from "../../assets/iconsSVG";
import signOut from "../../services/signOut";
import { AuthState } from "../../store/ducks/auth/types";
import ItemListSideMenu from "../itemListSideMenu";
import LogoutOverlay from "../LogoutOverlay";
import * as Styles from "./styles";
import { useLinksUteis } from "../../services/api/DadosComum/links-uteis";
import LogoSmall from "../../assets/logo/logo-it.png";
import Logo from "../../assets/logo/logo-white.png";
import variavel from "../../enviroments";
import { useStyles } from "../../constants/styles";
import materialUI from "../../material-UI";

// Estilos específicos da sidebar usando makeStyles (fora do componente para evitar recriação)
const useSidebarStyles = materialUI.makeStyles((theme) => ({
  sidebarWrapper: {
    position: "fixed",
    left: "-8px",
    top: "16px",
    height: "calc(100vh - 16px)",
    zIndex: 1200,
    transition: "width 0.3s ease",
    overflow: "hidden",
  },
  sidebarContainer: {
    position: "relative",
    left: "8px",
    top: 0,
    height: "100%",
    backgroundColor: "#2d5bff",
    transition: "width 0.3s ease",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: "20px",
    borderBottomRightRadius: 0,
    paddingTop: "16px",
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    backgroundColor: "#2d5bff",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    paddingBottom: "2px",
    position: "relative",
    flex: 1,
    overflow: "hidden",
  },
  list: {
    paddingTop: "8px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    overflow: "hidden",
  },
  groupItems: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingBottom: "16px",
    zIndex: 1,
    gap: "4px",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "rgba(255, 255, 255, 0.1)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(255, 255, 255, 0.3)",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "rgba(255, 255, 255, 0.5)",
    },
  },
}));

export default function PersistentDrawerLeft(): JSX.Element {
  const location = useLocation();
  const {
    urlFoto,
    name,
    apelido,
    codigoRegional365,
    usuarioCia,
    usuarioComercial,
    permissoes,
    supervisorProduto,
    codigoCargo2013,
    possuiToken3cplus,
    usuarioBackofficeVivaz,
  } = useSelector<RootStateOrAny, AuthState>((state) => state.user);

  const { data: linksUteis } = useLinksUteis();

  const possuiToken3cplusValid: boolean = possuiToken3cplus ?? false;
  const canAccessCampanhas =
    !permissoes.includes("tela_atendimento_visita") && possuiToken3cplusValid;

  const isAutonomo = localStorage.getItem("isAutonomo") === "true";
  const [sugestaoModal, setSugestaoModal] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const isBackofficeVivaz = usuarioBackofficeVivaz === true;

  const isRecepcionista =
    !supervisorProduto && permissoes.includes("tela_atendimento_visita");

  const isSupervisorProduto = codigoCargo2013 === CARGO_CODES.SUPERVISOR;

  const isCoordenadorProduto =
    codigoCargo2013 === CARGO_CODES.COORDENADOR_PRODUTO;
  const isCorretor = codigoCargo2013 === CARGO_CODES.CORRETOR;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const classes = useStyles();
  const sidebarClasses = useSidebarStyles();

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsExpanded(false);
    }, 160);
  };

  React.useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const hidden = location.pathname === "/block";

  // Atualiza variável CSS global para o layout
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      hidden ? "0px" : "80px"
    );
  }, [hidden]);

  const typeAmbiente = () => {
    if (variavel.AMBIENTE === "PROD") {
      return "";
    }
    if (variavel.AMBIENTE === "QA") {
      return "AMBIENTE DE QA";
    }
    if (variavel.AMBIENTE === "DEV") {
      return "AMBIENTE DE DESENVOLVIMENTO";
    }
    return "";
  };

  const typeCargo = () => {
    if (variavel.AMBIENTE !== "PROD") {
      switch (codigoCargo2013) {
        case CARGO_CODES.SUPERINTENDENTE:
          return "Superintendente";
        case CARGO_CODES.GERENTE_VENDAS:
          return "GerenteVendas";
        case CARGO_CODES.CORRETOR:
          return "Corretor";
        case CARGO_CODES.GERENTE_GERAL:
          return "GerenteGeral";
        case CARGO_CODES.VENDEDOR:
          return "Vendedor";
        case CARGO_CODES.SUPERVISOR:
          return "Supervisor";
        case CARGO_CODES.ESPECIALISTA_PRODUTO:
          return "EspecialistaProduto";
        case CARGO_CODES.RECEPCIONISTA:
          return "Recepcionista";
        case CARGO_CODES.ASSISTENTE_COMERCIAL:
          return "AssistenteComercial";
        case CARGO_CODES.GERENTE_COMERCIAL:
          return "GerenteComercial";
        case CARGO_CODES.DIRETOR:
          return "Diretor";
        case CARGO_CODES.COORDENADOR_CIA:
          return "CoordenadorCIA";
        case CARGO_CODES.GERENTE_CIA:
          return "GerenteCIA";
        case CARGO_CODES.COORDENADOR_PRODUTO:
          return "CoordenadorProduto";
        default:
          return "";
      }
    }
    return "";
  };

  const ambienteInfo = typeAmbiente();
  const cargoInfo = typeCargo();
  const showAmbienteInfo = ambienteInfo || cargoInfo;
  const logoRoute = isBackofficeVivaz ? "/funil-de-propostas" : "/menu";

  const handleDrawerClosePathname = (pathname: string) => {
    if (window.location.pathname === pathname) {
      window.location.reload();
    }
  };

  const handleSugestaoModal = (open: boolean) => {
    setTimeout(
      () => {
        setSugestaoModal(open);
      },
      open ? 100 : 0
    );
  };

  const exitToApp = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    signOut();
  };

  const [expanded, setExpanded] = React.useState<boolean>(false);

  const AccordionLinksUteis = React.useMemo(
    () => (
      <Styles.AccordionStyled
        expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        isExpanded={isExpanded}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          {!isExpanded ? (
            <Styles.FlexContainer isExpanded={isExpanded}>
              <IconsSVG.LinkIcon color="#FFFFFF" />
            </Styles.FlexContainer>
          ) : (
            <>
              <Styles.FlexContainer isExpanded={isExpanded}>
                <IconsSVG.LinkIcon color="#FFFFFF" />
                <p>Links úteis</p>
              </Styles.FlexContainer>
              <Styles.ButtonArrow
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                aria-expanded={expanded}
                aria-label="show more"
              >
                {expanded ? (
                  <IconsSVG.ExpandIcon color="#FFFFFF" rotate={false} />
                ) : (
                  <IconsSVG.ExpandIcon color="#FFFFFF" rotate />
                )}
              </Styles.ButtonArrow>
            </>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Styles.LinkList isExpanded={isExpanded}>
            {linksUteis && Array?.isArray(linksUteis) && linksUteis?.length > 0
              ? linksUteis?.map((link: any) => {
                  const url = link?.url || "";
                  const urlWithProtocol =
                    url.startsWith("http://") || url.startsWith("https://")
                      ? url
                      : `https://${url}`;
                  return (
                    <li key={link?.id}>
                      <a
                        href={urlWithProtocol}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link?.nome}
                      </a>
                    </li>
                  );
                })
              : null}
          </Styles.LinkList>
        </AccordionDetails>
      </Styles.AccordionStyled>
    ),
    [expanded, linksUteis, isExpanded]
  );

  return (
    <Styles.Container hidden={hidden}>
      <div
        className={sidebarClasses.sidebarWrapper}
        style={{
          width: `calc(${isExpanded ? "320px" : "80px"} + 8px)`,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className={sidebarClasses.sidebarContainer}
          style={
            {
              width: isExpanded ? "320px" : "80px",
              "--sidebar-width": isExpanded ? "320px" : "80px",
            } as React.CSSProperties
          }
        >
          <Styles.SidebarShadow isExpanded={isExpanded} />
          <div className={sidebarClasses.nav}>
            <Styles.HeaderSection
              isExpanded={isExpanded}
              isProd={variavel.AMBIENTE === "PROD"}
            >
              <Styles.SellitLogo isExpanded={isExpanded}>
                <a href={logoRoute} aria-label="Ir para o menu principal">
                  <img
                    src={isExpanded ? Logo : LogoSmall}
                    alt="logo sell.it"
                    className={isExpanded ? "logo-full" : "logo-small"}
                  />
                </a>
              </Styles.SellitLogo>
              {showAmbienteInfo && (
                <Styles.AmbienteInfo isExpanded={isExpanded}>
                  {ambienteInfo && (
                    <Styles.AmbienteBadge isExpanded={isExpanded}>
                      {isExpanded ? ambienteInfo : variavel.AMBIENTE}
                    </Styles.AmbienteBadge>
                  )}
                  {cargoInfo && (
                    <Styles.CargoBadge isExpanded={isExpanded}>
                      {isExpanded
                        ? cargoInfo
                        : cargoInfo.slice(0, 3).toUpperCase()}
                    </Styles.CargoBadge>
                  )}
                </Styles.AmbienteInfo>
              )}
            </Styles.HeaderSection>
            <div className={sidebarClasses.list}>
              <div
                className={sidebarClasses.groupItems}
                style={{
                  alignItems: isExpanded ? "stretch" : "center",
                }}
              >
                {isBackofficeVivaz ? (
                  <>
                    <ItemListSideMenu
                      name="Funil de propostas"
                      icon={<IconsSVG.FunilPropostasIcon />}
                      route="/funil-de-propostas"
                      onPress={() =>
                        handleDrawerClosePathname("/funil-de-propostas")
                      }
                      isExpanded={isExpanded}
                    />
                  </>
                ) : isRecepcionista ? (
                  // Renderizar apenas a opção "Roleta" para recepcionistas
                  <>
                    <ItemListSideMenu
                      name="Home"
                      icon={<IconsSVG.HomeIcon />}
                      route="/plantao"
                      onPress={() => handleDrawerClosePathname("/plantao")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Roleta"
                      icon={<IconsSVG.RoletaIcon />}
                      route="/roleta"
                      onPress={() => handleDrawerClosePathname("/roleta")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Carometro"
                      icon={<IoIdCardOutline />}
                      // tagNovo
                      route="/carometro"
                      onPress={() => handleDrawerClosePathname("/carometro")}
                      isExpanded={isExpanded}
                    />
                  </>
                ) : isSupervisorProduto ? (
                  // Renderizar menu específico para supervisor
                  <>
                    <ItemListSideMenu
                      name="Home"
                      icon={<IconsSVG.HomeIcon />}
                      route="/menu"
                      onPress={() => handleDrawerClosePathname("/menu")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="PDV"
                      icon={<IoStorefrontOutline />}
                      route="/plantao"
                      onPress={() => handleDrawerClosePathname("/plantao")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Funil de propostas"
                      icon={<IconsSVG.FunilPropostasIcon />}
                      route="/funil-de-propostas"
                      onPress={() =>
                        handleDrawerClosePathname("/funil-de-propostas")
                      }
                      isExpanded={isExpanded}
                    />
                    {/* <ItemListSideMenu
                    name="Roleta"
                    icon={<IconsSVG.RoletaIcon />}
                    route="/roleta"
                    onPress={() => handleDrawerClosePathname("/roleta")}
                  /> */}
                    <ItemListSideMenu
                      name="Carometro"
                      icon={<IoIdCardOutline />}
                      // tagNovo
                      route="/carometro"
                      onPress={() => handleDrawerClosePathname("/carometro")}
                      isExpanded={isExpanded}
                    />
                    {isAutonomo && (
                      <>
                        <ItemListSideMenu
                          name="Perfil"
                          icon={<CgProfile />}
                          route="/perfil"
                          onPress={() => handleDrawerClosePathname("/perfil")}
                          isExpanded={isExpanded}
                        />
                        <ItemListSideMenu
                          name="Sugestão"
                          icon={<BiCommentAdd />}
                          onPress={() => handleSugestaoModal(true)}
                          isExpanded={isExpanded}
                        />
                      </>
                    )}
                    <ItemListSideMenu
                      name="Solicitação de Visita"
                      icon={<FaCalendarAlt />}
                      route="/visitas"
                      onPress={() => handleDrawerClosePathname("/visitas")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Calendário"
                      icon={<FaRegCalendarCheck />}
                      route="/calendario"
                      onPress={() => handleDrawerClosePathname("/calendario")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    <ItemListSideMenu
                      name="Destaques"
                      icon={<FaRegStar />}
                      // tagNovo
                      route="/destaques"
                      onPress={() => handleDrawerClosePathname("/destaques")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Produtos"
                      icon={<IconsSVG.ProductPredio2 />}
                      route="/produtos"
                      onPress={() => handleDrawerClosePathname("/produtos")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Academia sell.it"
                      icon={<IconsSVG.AcademiaIcon />}
                      // tagNovo
                      route="/academia"
                      onPress={() => handleDrawerClosePathname("/academia")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Onboarding"
                      icon={<LuUserPlus />}
                      route="/cadastro-cyrela"
                      onPress={() =>
                        handleDrawerClosePathname("/cadastro-cyrela")
                      }
                      isExpanded={isExpanded}
                    />
                    {/* Accordion "Links úteis" */}
                    {AccordionLinksUteis}
                  </>
                ) : isCoordenadorProduto ? (
                  // Renderizar menu específico para supervisor e coordenador de produto
                  <>
                    <ItemListSideMenu
                      name="Home"
                      icon={<IconsSVG.HomeIcon />}
                      route="/menu"
                      onPress={() => handleDrawerClosePathname("/menu")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Funil de propostas"
                      icon={<IconsSVG.FunilPropostasIcon />}
                      route="/funil-de-propostas"
                      onPress={() =>
                        handleDrawerClosePathname("/funil-de-propostas")
                      }
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Produtos"
                      icon={<IconsSVG.ProductPredio2 />}
                      route="/produtos"
                      onPress={() => handleDrawerClosePathname("/produtos")}
                      isExpanded={isExpanded}
                    />
                    {isAutonomo && (
                      <>
                        <ItemListSideMenu
                          name="Perfil"
                          icon={<CgProfile />}
                          route="/perfil"
                          onPress={() => handleDrawerClosePathname("/perfil")}
                          isExpanded={isExpanded}
                        />
                        <ItemListSideMenu
                          name="Sugestão"
                          icon={<BiCommentAdd />}
                          onPress={() => handleSugestaoModal(true)}
                          isExpanded={isExpanded}
                        />
                      </>
                    )}
                    <ItemListSideMenu
                      name="Solicitação de Visita"
                      icon={<FaCalendarAlt />}
                      route="/visitas"
                      onPress={() => handleDrawerClosePathname("/visitas")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    <ItemListSideMenu
                      name="Calendário"
                      icon={<FaRegCalendarCheck />}
                      route="/calendario"
                      onPress={() => handleDrawerClosePathname("/calendario")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    <ItemListSideMenu
                      name="Carometro"
                      icon={<IoIdCardOutline />}
                      // tagNovo
                      route="/carometro"
                      onPress={() => handleDrawerClosePathname("/carometro")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Destaques"
                      icon={<FaRegStar />}
                      // tagNovo
                      route="/destaques"
                      onPress={() => handleDrawerClosePathname("/destaques")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Academia sell.it"
                      icon={<IconsSVG.AcademiaIcon />}
                      // tagNovo
                      route="/academia"
                      onPress={() => handleDrawerClosePathname("/academia")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Onboarding"
                      icon={<LuUserPlus />}
                      route="/cadastro-cyrela"
                      onPress={() =>
                        handleDrawerClosePathname("/cadastro-cyrela")
                      }
                      isExpanded={isExpanded}
                    />
                    {/* Accordion "Links úteis" */}
                    {AccordionLinksUteis}
                  </>
                ) : permissoes?.includes("conexao_revenda_esconder") &&
                  permissoes.includes("conexao_revenda") ? (
                  // Renderizar apenas menus compatíveis com o acesso de revenda
                  <>
                    <ItemListSideMenu
                      name="Home"
                      icon={<IconsSVG.HomeIcon />}
                      route="/menu"
                      onPress={() => handleDrawerClosePathname("/menu")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Produtos"
                      icon={<IconsSVG.ProductPredio2 />}
                      route="/produtos"
                      onPress={() => handleDrawerClosePathname("/produtos")}
                      isExpanded={isExpanded}
                    />
                    {isAutonomo && (
                      <>
                        <ItemListSideMenu
                          name="Perfil"
                          icon={<CgProfile />}
                          route="/perfil"
                          onPress={() => handleDrawerClosePathname("/perfil")}
                          isExpanded={isExpanded}
                        />
                        <ItemListSideMenu
                          name="Sugestão"
                          icon={<BiCommentAdd />}
                          onPress={() => handleSugestaoModal(true)}
                          isExpanded={isExpanded}
                        />
                      </>
                    )}
                    <ItemListSideMenu
                      name="Solicitação de Visita"
                      icon={<FaCalendarAlt />}
                      route="/visitas"
                      onPress={() => handleDrawerClosePathname("/visitas")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    <ItemListSideMenu
                      name="Calendário"
                      icon={<FaRegCalendarCheck />}
                      route="/calendario"
                      onPress={() => handleDrawerClosePathname("/calendario")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    <ItemListSideMenu
                      name="Revenda"
                      icon={<RiHotelLine />}
                      route="/revenda"
                      onPress={() => handleDrawerClosePathname("/revenda")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Onboarding"
                      icon={<LuUserPlus />}
                      route="/cadastro-cyrela"
                      onPress={() =>
                        handleDrawerClosePathname("/cadastro-cyrela")
                      }
                      isExpanded={isExpanded}
                    />
                    {/* Accordion "Links úteis" */}
                    {AccordionLinksUteis}
                  </>
                ) : (
                  // Renderizar todas as outras opções para outros usuários
                  <>
                    {/* Item "Home" para todos os usuários */}
                    <ItemListSideMenu
                      name="Home"
                      icon={<IconsSVG.HomeIcon />}
                      route="/menu"
                      onPress={() => handleDrawerClosePathname("/menu")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Leads"
                      icon={<IoPeople />}
                      route="/leads-infos"
                      onPress={() => handleDrawerClosePathname("/leads-infos")}
                      isExpanded={isExpanded}
                    />
                    {/* <ItemListSideMenu
                    name="Carteira"
                    icon={<IconsSVG.WalletIcon />}
                    route="/leads"
                    onPress={() => handleDrawerClosePathname("/leads")}
                  /> */}
                    <ItemListSideMenu
                      name="Academia sell.it"
                      icon={<IconsSVG.AcademiaIcon />}
                      // tagNovo
                      route="/academia"
                      onPress={() => handleDrawerClosePathname("/academia")}
                      isExpanded={isExpanded}
                    />
                    {/* <ItemListSideMenu
                    name="Estatisticas"
                    icon={<IconsSVG.EstatisticasIcon />}
                    // tagNovo
                    route="/estatisticas"
                    onPress={() => handleDrawerClosePathname("/estatisticas")}
                  /> */}
                    <ItemListSideMenu
                      name="Destaques"
                      icon={<FaRegStar />}
                      // tagNovo
                      route="/destaques"
                      onPress={() => handleDrawerClosePathname("/destaques")}
                      isExpanded={isExpanded}
                    />
                    {isCorretor && (
                      <>
                        <ItemListSideMenu
                          name="Roleta"
                          icon={<IconsSVG.RoletaIcon />}
                          route="/roleta"
                          onPress={() => handleDrawerClosePathname("/roleta")}
                          isExpanded={isExpanded}
                        />
                      </>
                    )}
                    <ItemListSideMenu
                      name="Checkin"
                      icon={<IconsSVG.ProductPredio2 />}
                      route="/checkin"
                      onPress={() => handleDrawerClosePathname("/checkin")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Funil de propostas"
                      icon={<IconsSVG.FunilPropostasIcon />}
                      route="/funil-de-propostas"
                      onPress={() =>
                        handleDrawerClosePathname("/funil-de-propostas")
                      }
                      isExpanded={isExpanded}
                    />
                    {usuarioComercial && (
                      <ItemListSideMenu
                        name="Funil Comercial"
                        icon={<IconsSVG.FunilPropostasIcon />}
                        route="/funil-comercial"
                        onPress={() =>
                          handleDrawerClosePathname("/funil-comercial")
                        }
                        isExpanded={isExpanded}
                      />
                    )}

                    {/* Outras Opções de Menu */}
                    {canAccessCampanhas && (
                      <ItemListSideMenu
                        name="Oferta Ativa"
                        icon={<IoVolumeHighOutline color="#FFFFFF" />}
                        // tagNovo
                        route="/campanhas-criadas"
                        onPress={() =>
                          handleDrawerClosePathname("/campanhas-criadas")
                        }
                        isExpanded={isExpanded}
                      />
                    )}
                    <ItemListSideMenu
                      name="Mesa de telefone"
                      icon={<IconsSVG.MesaTelefoneIcon />}
                      route="/mesa-telefone"
                      onPress={() =>
                        handleDrawerClosePathname("/mesa-telefone")
                      }
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="WhatsApp"
                      icon={<IoLogoWhatsapp color="#FFFFFF" />}
                      route="/whatsapp"
                      onPress={() => handleDrawerClosePathname("/whatsapp")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Produtos"
                      icon={<IconsSVG.ProductPredio2 />}
                      route="/produtos"
                      onPress={() => handleDrawerClosePathname("/produtos")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Carometro"
                      icon={<IoIdCardOutline />}
                      // tagNovo
                      route="/carometro"
                      onPress={() => handleDrawerClosePathname("/carometro")}
                      isExpanded={isExpanded}
                    />
                    {canAccessCampanhas && (
                      <ItemListSideMenu
                        name="Oferta Ativa"
                        icon={<IoVolumeHighOutline />}
                        // tagNovo
                        route="/campanhas-criadas"
                        onPress={() =>
                          handleDrawerClosePathname("/campanhas-criadas")
                        }
                        isExpanded={isExpanded}
                      />
                    )}

                    {/* Condicional: Exibir "Perfil" apenas para autônomos */}
                    {isAutonomo && (
                      <>
                        <ItemListSideMenu
                          name="Perfil"
                          icon={<CgProfile />}
                          route="/perfil"
                          onPress={() => handleDrawerClosePathname("/perfil")}
                          isExpanded={isExpanded}
                        />
                        <ItemListSideMenu
                          name="Sugestão"
                          icon={<BiCommentAdd />}
                          onPress={() => handleSugestaoModal(true)}
                          isExpanded={isExpanded}
                        />
                      </>
                    )}
                    <ItemListSideMenu
                      name="Solicitação de Visita"
                      icon={<FaCalendarAlt />}
                      route="/visitas"
                      onPress={() => handleDrawerClosePathname("/visitas")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Calendário"
                      icon={<FaRegCalendarCheck />}
                      route="/calendario"
                      onPress={() => handleDrawerClosePathname("/calendario")}
                      isExpanded={isExpanded}
                    />
                    <ItemListSideMenu
                      name="Controle de Visitas"
                      icon={<FaPeopleGroup color="#FFFFFF" />}
                      route="/controle-visitas"
                      onPress={() =>
                        handleDrawerClosePathname("/controle-visitas")
                      }
                    />
                    {permissoes.includes("conexao_revenda") && (
                      <ItemListSideMenu
                        name="Revenda"
                        icon={<RiHotelLine />}
                        route="/revenda"
                        onPress={() => handleDrawerClosePathname("/revenda")}
                        isExpanded={isExpanded}
                      />
                    )}
                    <ItemListSideMenu
                      name="Onboarding"
                      icon={<LuUserPlus />}
                      route="/cadastro-cyrela"
                      onPress={() =>
                        handleDrawerClosePathname("/cadastro-cyrela")
                      }
                      isExpanded={isExpanded}
                    />
                    {/* Accordion "Links úteis" */}
                    {AccordionLinksUteis}
                  </>
                )}
              </div>
              {/* Seção de Logout */}
              <Styles.FooterLogoutSection isExpanded={isExpanded}>
                <div
                  style={{
                    display: "flex",
                    gap: isExpanded ? 24 : 0,
                    padding: isExpanded ? "20px" : "10px",
                    alignItems: "center",
                    justifyContent: isExpanded ? "flex-start" : "center",
                    transition: "all 0.3s ease",
                  }}
                >
                  <Styles.UserAvatar isExpanded={isExpanded}>
                    {urlFoto ? (
                      <Styles.ImgAvatar
                        src={urlFoto}
                        style={{
                          width: isExpanded ? 50 : 40,
                          height: isExpanded ? 50 : 40,
                        }}
                        alt="User Avatar"
                      />
                    ) : (
                      <Styles.AvatarBox isExpanded={isExpanded}>
                        <IconsSVG.UserIcon />
                      </Styles.AvatarBox>
                    )}
                  </Styles.UserAvatar>
                  <Styles.UserData isExpanded={isExpanded}>
                    <Styles.UserName isExpanded={isExpanded}>
                      Olá,
                    </Styles.UserName>
                    <Styles.UserName isExpanded={isExpanded}>
                      {apelido || name}
                    </Styles.UserName>
                  </Styles.UserData>
                </div>
                <ItemListSideMenu
                  name="Sair"
                  icon={<IconsSVG.LogoutIcon />}
                  onPress={() => {
                    exitToApp();
                  }}
                  isExpanded={isExpanded}
                />
              </Styles.FooterLogoutSection>
            </div>
          </div>
        </div>
      </div>
      {/* Modal de Sugestão */}
      <ModalNovaSugestao
        open={sugestaoModal}
        onClose={() => handleSugestaoModal(false)}
      />
      <LogoutOverlay open={isLoggingOut} />
    </Styles.Container>
  );
}
